/**
 * RunNowCard — admin-only card on the System Status page that lets the
 * admin manually invoke any of the 5 user-facing scheduled jobs without
 * waiting for their cron time.
 *
 * Wiring:
 *   GET  /api/admin/scheduler/run-now/registry  → list of {id, label, description}
 *   POST /api/admin/scheduler/run-now/{job_id}  → fires the job in a backend
 *                                                 background task, returns
 *                                                 immediately. The actual
 *                                                 work runs out-of-band.
 *   POST /api/admin/recompute-scores/{symbol}   → recompute composite
 *                                                 score for ONE symbol,
 *                                                 synchronously (~0.5-1s).
 *                                                 Used by the "Specific
 *                                                 stock" branch below so
 *                                                 admins can validate a
 *                                                 new report's scoring
 *                                                 impact without waiting
 *                                                 for the 284-symbol
 *                                                 sweep.
 *
 * UX:
 *   • One row per job. Each row is a Card-like surface with the label,
 *     description, and a [▶ Run Now] button.
 *   • Composite Score row also offers "Specific stock" mode: the admin
 *     enters/selects a symbol and the card calls the single-stock
 *     endpoint instead of the global sweep.
 *   • While a job is in flight (locally) the button shows a spinner and
 *     "Running…" — preventing double-clicks. We unlock after a fixed
 *     duration (default 25s) since the backend doesn't expose a "still
 *     running?" probe per job (it's fire-and-forget).
 *   • Two toasts per click:
 *       1. immediate  "Started — Score Recompute" (info)
 *       2. delayed    "Completed — refreshing data…" (success) so admin
 *                     gets confirmation that the background job has had
 *                     enough wall-clock time to finish.
 *   • For long-running jobs (Fundamentals, Sector Analysis) we surface
 *     a longer "this can take 3-6 minutes" toast on click.
 *
 * Why a separate component (vs inline in SystemStatusPage.js)?
 *   The page is already 800+ lines. Encapsulating the new feature here
 *   makes the diff easy to review and lets us drop the card cleanly
 *   above the existing scheduler list.
 */
import React, { useEffect, useMemo, useState } from "react";
import { apiGet, apiPost } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Play, Zap, Target } from "lucide-react";
import { toast } from "sonner";

// Per-job UX hints. Long-running jobs surface a different "this is
// expected" toast so the admin doesn't refresh the page anxiously.
//
// `expectedSeconds` drives the local "running" lock so the button
// re-enables roughly when the work is expected to finish. The backend
// fires-and-forgets — there's no per-job status probe — so this is a
// best-effort UX hint, not a hard guarantee.
const JOB_HINTS = {
  composite_scores_refresh: {
    expectedSeconds: 5,
    longRunNote: null,
  },
  prices_refresh: {
    expectedSeconds: 60,
    longRunNote: "Scraping CSE for ~284 securities — this takes about 30-60s.",
  },
  tv_fundamentals: {
    expectedSeconds: 180,
    longRunNote: "Refreshing TradingView fundamentals — this can take 2-3 minutes.",
  },
  sector_analysis_refresh: {
    expectedSeconds: 360,
    longRunNote: "Running Claude across all CSE sectors — 4-6 minutes, ≈ $0.25-$1.",
  },
  dividend_announcements_sync: {
    expectedSeconds: 90,
    longRunNote: "Pulling declared dividends from CSE approvedAnnouncement API.",
  },
  news_refresh: {
    expectedSeconds: 90,
    longRunNote: "Fetching FT.lk RSS, running sentiment + company matching.",
  },
};

const RunNowCard = () => {
  const [jobs, setJobs] = useState([]);
  const [running, setRunning] = useState({});         // jobId -> bool
  const [loading, setLoading] = useState(true);

  // "Specific stock" recompute state — only exposed on the
  // composite_scores_refresh row.
  const [scope, setScope] = useState("all");          // "all" | "single"
  const [symbols, setSymbols] = useState([]);         // full symbol list for the combobox
  const [symbolsLoaded, setSymbolsLoaded] = useState(false);
  const [symbolFilter, setSymbolFilter] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [singleRunning, setSingleRunning] = useState(false);

  useEffect(() => {
    apiGet("/admin/scheduler/run-now/registry")
      .then((d) => setJobs(d.jobs || []))
      .catch(() => setJobs([]))
      .finally(() => setLoading(false));
  }, []);

  // Lazy-load the securities list the first time the admin flips the
  // composite-scores row to "Specific stock" — avoids a 284-row
  // payload on every System Status page visit.
  const ensureSymbolsLoaded = async () => {
    if (symbolsLoaded) return;
    try {
      const rows = await apiGet("/securities");
      const uniq = Array.from(
        new Set(
          (Array.isArray(rows) ? rows : [])
            .map((r) => r?.cse_symbol || r?.symbol)
            .filter(Boolean),
        ),
      ).sort();
      setSymbols(uniq);
      setSymbolsLoaded(true);
    } catch {
      toast.error("Could not load the symbol list — try again in a moment.");
    }
  };

  const filteredSymbols = useMemo(() => {
    const q = symbolFilter.trim().toUpperCase();
    if (!q) return symbols.slice(0, 50); // cap render size for snappy select
    return symbols.filter((s) => s.toUpperCase().includes(q)).slice(0, 50);
  }, [symbols, symbolFilter]);

  const trigger = async (job) => {
    if (running[job.id]) return;
    const hint = JOB_HINTS[job.id] || { expectedSeconds: 30, longRunNote: null };
    setRunning((prev) => ({ ...prev, [job.id]: true }));

    try {
      const res = await apiPost(`/admin/scheduler/run-now/${job.id}`);
      // Started toast — fires immediately. For long-running jobs include
      // the wall-clock heads-up so admin doesn't refresh in 10s and
      // panic when nothing's changed yet.
      const startMsg = hint.longRunNote
        ? `Started — ${job.label}. ${hint.longRunNote}`
        : `Started — ${job.label}`;
      toast.info(startMsg, {
        description: res?.note || "Running in the background.",
        duration: 4000,
      });
    } catch (e) {
      setRunning((prev) => ({ ...prev, [job.id]: false }));
      toast.error(
        e?.response?.data?.detail || `Failed to start ${job.label}`,
        { duration: 5000 },
      );
      return;
    }

    // Re-enable button + show completion toast after the expected
    // runtime. We use `setTimeout` rather than polling because the
    // backend doesn't expose a per-job liveness endpoint and adding
    // one would balloon scope; the wall-clock estimate is accurate
    // enough for admin UX.
    const releaseMs = Math.max(2000, hint.expectedSeconds * 1000);
    setTimeout(() => {
      setRunning((prev) => ({ ...prev, [job.id]: false }));
      toast.success(`Completed — ${job.label}`, {
        description: "Refresh the page to see updated data.",
        duration: 4000,
      });
    }, releaseMs);
  };

  // Synchronous single-symbol recompute — hits the dedicated admin
  // endpoint and surfaces a BEFORE → AFTER delta toast.
  const triggerSingle = async () => {
    const sym = (selectedSymbol || "").trim().toUpperCase();
    if (!sym) {
      toast.error("Select a symbol first.");
      return;
    }
    setSingleRunning(true);
    try {
      const res = await apiPost(`/admin/recompute-scores/${sym}`);
      if (!res?.ok) {
        toast.info(res?.message || `${sym}: not enough data to compute a score yet.`);
        return;
      }
      const prev = res.previous || {};
      const curr = res.current || {};
      const hasPrev = prev.final_score != null || prev.signal;
      const fmt = (v) => (v == null ? "—" : v);
      const desc = hasPrev
        ? `Score: ${fmt(prev.final_score)} → ${fmt(curr.final_score)} · Signal: ${fmt(prev.signal)} → ${fmt(curr.signal)}`
        : `Score: ${fmt(curr.final_score)} · Signal: ${fmt(curr.signal)}`;
      if (res.changed) {
        toast.success(`${sym} · Recalculated`, { description: desc, duration: 7000 });
      } else {
        toast.info(`${sym} · Score unchanged`, { description: desc, duration: 5000 });
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Recompute failed for ${sym}`,
        { duration: 5000 },
      );
    } finally {
      setSingleRunning(false);
    }
  };

  if (loading) {
    return (
      <Card className="border bg-card" data-testid="run-now-card-loading">
        <CardHeader className="pb-3">
          <CardTitle className="font-display flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" /> Run scheduled jobs now
          </CardTitle>
        </CardHeader>
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" />
          Loading job registry…
        </CardContent>
      </Card>
    );
  }

  if (!jobs.length) return null;

  return (
    <Card className="border bg-card" data-testid="run-now-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="font-display flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" /> Run scheduled jobs now
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Trigger any of these jobs immediately without waiting for the cron time.
              Each fires in a background worker so the page stays responsive.
            </p>
          </div>
          <Badge variant="outline" className="gap-1.5 shrink-0">
            <Play className="h-3 w-3" />
            Admin only
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border rounded-lg border bg-background overflow-hidden">
          {jobs.map((j) => {
            const isRunning = !!running[j.id];
            const isScoreRow = j.id === "composite_scores_refresh";
            return (
              <li
                key={j.id}
                className="px-4 py-3 flex flex-col gap-3"
                data-testid={`run-now-row-${j.id}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium" data-testid={`run-now-label-${j.id}`}>
                      {j.label}
                    </div>
                    <div className="text-[11px] text-muted-foreground leading-snug">
                      {j.description}
                    </div>
                  </div>
                  {/* Default "Run Now" stays visible unless admin switched
                      the composite-score row to "single" mode — then the
                      per-symbol action takes over and this would be a
                      misleading duplicate. */}
                  {!(isScoreRow && scope === "single") && (
                    <Button
                      size="sm"
                      variant={isRunning ? "secondary" : "default"}
                      onClick={() => trigger(j)}
                      disabled={isRunning}
                      className="gap-1.5 shrink-0 min-w-[110px] justify-center"
                      data-testid={`run-now-btn-${j.id}`}
                    >
                      {isRunning ? (
                        <>
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Running…
                        </>
                      ) : (
                        <>
                          <Play className="h-3.5 w-3.5" />
                          Run Now
                        </>
                      )}
                    </Button>
                  )}
                </div>

                {/* Scope selector + single-stock input — only rendered
                    on the Score Recompute row. */}
                {isScoreRow && (
                  <div className="rounded-md border border-dashed bg-muted/30 p-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                      <div className="text-[11px] font-medium text-foreground flex items-center gap-1.5">
                        <Target className="h-3 w-3 text-primary" />
                        Scope
                      </div>
                      <Select
                        value={scope}
                        onValueChange={(v) => {
                          setScope(v);
                          if (v === "single") ensureSymbolsLoaded();
                        }}
                      >
                        <SelectTrigger
                          className="h-8 w-[180px]"
                          data-testid="run-now-score-scope-trigger"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all" data-testid="run-now-score-scope-all">
                            All stocks (~284)
                          </SelectItem>
                          <SelectItem value="single" data-testid="run-now-score-scope-single">
                            Specific stock
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {scope === "single" && (
                      <div className="flex items-end gap-2 flex-wrap">
                        <div className="flex-1 min-w-[160px]">
                          <label className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1 block">
                            Symbol
                          </label>
                          <div className="flex gap-2">
                            <Input
                              value={symbolFilter}
                              onChange={(e) => setSymbolFilter(e.target.value.toUpperCase())}
                              placeholder="Filter e.g. COMB"
                              className="h-8 w-[140px]"
                              data-testid="run-now-score-symbol-filter"
                            />
                            <Select
                              value={selectedSymbol}
                              onValueChange={setSelectedSymbol}
                            >
                              <SelectTrigger
                                className="h-8 w-[180px]"
                                data-testid="run-now-score-symbol-trigger"
                              >
                                <SelectValue
                                  placeholder={symbolsLoaded ? "Pick symbol" : "Loading…"}
                                />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {filteredSymbols.length === 0 ? (
                                  <div className="px-2 py-6 text-center text-xs text-muted-foreground">
                                    {symbolsLoaded ? "No matching symbols" : "Loading…"}
                                  </div>
                                ) : (
                                  filteredSymbols.map((s) => (
                                    <SelectItem
                                      key={s}
                                      value={s}
                                      data-testid={`run-now-score-symbol-option-${s}`}
                                    >
                                      {s}
                                    </SelectItem>
                                  ))
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="default"
                          onClick={triggerSingle}
                          disabled={singleRunning || !selectedSymbol}
                          className="gap-1.5 h-8"
                          data-testid="run-now-score-single-button"
                        >
                          {singleRunning ? (
                            <>
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Recalculating…
                            </>
                          ) : (
                            <>
                              <Play className="h-3.5 w-3.5" />
                              Recalculate
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                    {scope === "single" && (
                      <p className="mt-2 text-[10.5px] text-muted-foreground leading-snug">
                        Single-stock recompute is synchronous (~0.5-1s) and returns a
                        before/after comparison. Use this right after uploading a report
                        for instant scoring feedback.
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
};

export default RunNowCard;
